import React, { useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ReCAPTCHA from 'react-google-recaptcha';
import { trackEvent } from '../../analytics';
import styles from './CreateAccount.module.css';

export function CreateAccount() {
  const navigate = useNavigate();
  const { addUser, isAuthenticated } = useAuth();
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const hasTrackedStartRef = useRef(false);

  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

  const trackSignupStarted = () => {
    if (hasTrackedStartRef.current) return;
    hasTrackedStartRef.current = true;
    trackEvent('signup_started', { entry: 'create_account_form' });
  };

  if (isAuthenticated) {
    navigate('/', { replace: true });
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    trackSignupStarted();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (recaptchaSiteKey && !recaptchaToken) {
      setError('Please complete the CAPTCHA.');
      return;
    }
    setLoading(true);
    try {
      const result = await addUser({
        username,
        displayName,
        email,
        password,
        ...(recaptchaSiteKey && recaptchaToken && { recaptchaToken }),
      });
      if (result.success) {
        trackEvent('signup_completed', {
          entry: 'create_account_form',
          verification_required: true,
        });
        // After creating an account, send the user to the login page
        // where they'll see instructions to check their email.
        navigate('/login?checkEmail=1', { replace: true });
      } else {
        setError(result.error);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <h1>Create account</h1>
        <p className={styles.subtitle}>Join RideSpot and share your spots and garage.</p>
        <p className={styles.support}>
          Create your account now, then verify your email to start posting, building your garage, and joining the leaderboards.
        </p>
        <form onSubmit={handleSubmit} onFocusCapture={trackSignupStarted}>
          <label>
            Username
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="e.g. petrolhead"
              autoComplete="username"
              required
            />
          </label>
          <label>
            Display name
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Alex"
              autoComplete="name"
              required
            />
          </label>
          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="e.g. alex@example.com"
              autoComplete="email"
              required
            />
          </label>
          <label>
            Password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 4 characters"
              autoComplete="new-password"
              required
              minLength={4}
            />
          </label>
          <label>
            Confirm password
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repeat password"
              autoComplete="new-password"
              required
            />
          </label>
          {recaptchaSiteKey && (
            <div className={styles.captchaRow}>
              <ReCAPTCHA
                sitekey={recaptchaSiteKey}
                onChange={(token: string | null) => {
                  setRecaptchaToken(token);
                  if (token && error === 'Please complete the CAPTCHA.') setError('');
                }}
              />
            </div>
          )}
          {error && <p className={styles.error}>{error}</p>}
          <button type="submit" className={styles.submit} disabled={loading}>
            {loading ? 'Creating...' : 'Create account'}
          </button>
        </form>
        <p className={styles.footer}>
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
